import React, {useState, useEffect, useRef} from 'react';
import {ContentDispatch} from 'framework';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {TabComponent} from 'componentLibrary/modules/TabComponent';
import REST from 'go/requests/rest';
import {
  formatDateMMDDYYYY,
  ISOToDisplayDate,
  getRfaActions,
  compileContent,
  isRecipientUser,
} from 'common/utils';
import {Pagination, Loader, Button, Input, Icon, Grid, Header} from 'semantic-ui-react';
import {withRouter} from 'react-router-dom';
import queryString from 'query-string';
import _ from 'lodash';
import RequestsForAssistanceTable from './RequestsForAssistanceTable';
import {
  ASSOCIATED_ENTITY_STATUSES,
  ROLE_MAP,
} from 'app-ui-componentlibrary/dist/common/constants';
import PermissionChecks, {extractOrgIdsFromPermissions} from 'go/security/permissions';
import {forceJsonResponse, MONO, FLUX} from 'common/utils';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const RequestsForAssistanceTableWrapper = (props) => {
  const {
    tabs,
    fundingOpportunityId,
    organizationId,
    fundingOpportunityData,
    permissions,
    programId,
  } = props;
  const pageSize = _.get(props, 'pageSize', 10);
  const defaultSort = _.get(props, 'defaultSort', 'statusTimeStamp');
  const defaultSortDirection = _.get(props, 'defaultSortDirection', 'ASC');
  const {header, headerSize, headerAggregate} = props;

  const noRfaMsg =
    fundingOpportunityData?.programId == 47
      ? 'There are no requests for fire management assistance available'
      : 'There are no requests for assistance available';

  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [requestQueue, setRequestQueue] = useState([]);
  const query = queryString.parse(_.get(props, 'location.search'));
  const [activeTabId, setActiveTabId] = useState(
    _.get(query, 'initialTab', _.get(tabs, '0.id'))
  );
  const [tabData, setTabData] = useState({});
  const tabsWithCount = _.map(tabs, (tab) => {
    const id = tab.id;
    return {
      ...tab,
      count: _.get(tabData, [id, 'count'], 0),
    };
  });
  const tab = _.find(tabsWithCount, (tab) => tab.id === activeTabId);
  const summary = _.get(tab, 'name', 'Requests for assistance') + ' table';
  const items = _.get(tabData, [activeTabId, 'items'], []);
  const tableProps = _.get(tab, 'tableProps', {});

  const [selectedColumn, setSelectedColumn] = useState(
    _.get(tableProps, 'defaultSort', defaultSort)
  );
  const [sortDirection, setSortDirection] = useState(
    _.get(tableProps, 'defaultSortDirection', defaultSortDirection)
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClearingSearchTerm, setIsClearingSearchTerm] = useState(false);
  const [tableHeight, setTableHeight] = useState('63px');
  const prevSearchTerm = usePrevious(searchTerm);
  const tableRef = useRef(null);
  const requestQueueRef = useRef();
  requestQueueRef.current = requestQueue;

  const activeTabFilteredCount = _.get(tabData, [activeTabId, 'filteredCount'], 0);
  const totalPages = Math.ceil(activeTabFilteredCount / pageSize);
  let showPrevButton = currentPage + 1 > 1 ? undefined : null;
  let showNextButton = currentPage + 1 < totalPages ? undefined : null;

  const [rfaDynamicData, setrfaDynamicData] = useState([]);

  const setActiveTab = (tabId) => {
    setActiveTabId(tabId);
    const activeTab = _.find(tabs, (tab) => tab.id === tabId);
    const tabDefaultSort = _.get(activeTab, 'tableProps.defaultSort', defaultSort);
    const tabDefaultSortDirection = _.get(
      activeTab,
      'tableProps.defaultSortDirection',
      defaultSortDirection
    );
    setSelectedColumn(tabDefaultSort);
    setSortDirection(tabDefaultSortDirection);
    setCurrentPage(0);
    setSearchTerm('');
  };

  const setSelectedColumnProp = (columnName) => {
    setSelectedColumn(columnName);
  };

  const setSortDirectionProp = (direction) => {
    setSortDirection(direction);
    setCurrentPage(0);
  };

  const handlePageChange = (e, {activePage}) => {
    setCurrentPage(activePage - 1);
  };

  const handleTermChange = (e, entry) => {
    setSearchTerm(entry.value);
    setCurrentPage(0);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    getTabContents(activeTabId);
  };

  const onClearSearchTerm = () => {
    setIsClearingSearchTerm(true);
    setSearchTerm('');
  };

  useEffect(() => {
    if (organizationId === undefined && !isRecipientUser(props.permissions)) {
      REST.dynamicData
        .getEntityByEntityTypeAndFOId('rfa', fundingOpportunityId)
        .then((rfaRes) => {
          return forceJsonResponse(rfaRes, MONO);
        })
        .then((data) => {
          setrfaDynamicData(data);
          getTabContents(activeTabId);
        })
        .catch((e) => {
          console.error('Error retrieving rfas');
        });
    } else {
      let userOrg;
      if (isRecipientUser(props.permissions))
        userOrg = extractOrgIdsFromPermissions(props.permissions)[0];
      REST.dynamicData
        .getEntityByOrgIdAndFOId(
          organizationId ? organizationId : userOrg,
          fundingOpportunityId,
          'rfa'
        )
        .then((rfaRes) => {
          return forceJsonResponse(rfaRes, MONO);
        })
        .then((data) => {
          setrfaDynamicData(data);
          getTabContents(activeTabId);
        })
        .catch((e) => {
          console.error('Error retrieving rfas');
        });
    }
  }, [props?.permissions]);

  useEffect(() => {
    if (!isInitialLoad) getTabContents(activeTabId);
  }, [selectedColumn, sortDirection, currentPage, activeTabId]);

  useEffect(() => {
    if (prevSearchTerm !== searchTerm && !isInitialLoad) {
      if (isClearingSearchTerm) {
        getTabContents(activeTabId);
        setIsClearingSearchTerm(false);
      } else {
        const timer = setTimeout(() => {
          getTabContents(activeTabId);
        }, 1200);

        return () => clearTimeout(timer);
      }
    }
  }, [prevSearchTerm, searchTerm, isClearingSearchTerm]);

  const getTabContents = (activeTab) => {
    setLoading(true);
    setRequestQueue([
      ...requestQueue,
      {
        type: 'getTabContentsAction',
        payload: {
          activeTab,
        },
      },
    ]);
  };

  useEffect(() => {
    if (requestQueue.length && !requestQueue[0].loading) {
      const action = {
        ...requestQueue[0],
      };
      action.loading = true;
      setRequestQueue([action, ...requestQueue.slice(1)]);
      const cb = () => {
        setRequestQueue(requestQueueRef.current.slice(1));
      };
      switch (action.type) {
        case 'getTabContentsAction':
          getTabContentsAction(cb, cb);
          break;
      }
    }
  }, [requestQueue]);

  const getTabContentsAction = (cb, errorcb) => {
    setLoading(true);
    const offset = currentPage * pageSize;
    let rfaRequests = [];

    if (_.get(tableRef, 'current')) {
      let height = tableRef.current.getBoundingClientRect().height;
      setTableHeight(height);
    }

    // iterate over tabs to retrieve rfa data
    _.forEach(tabs, (tab) => {
      const {id, statuses} = tab;
      rfaRequests.push(getRFAData(id, searchTerm, statuses, pageSize, offset));
    });

    Promise.all(rfaRequests)
      .then((rfasResults) => {
        let newTabData = _.cloneDeep(tabData);
        _.forEach(rfasResults, (rfaData) => {
          let tabName = rfaData.tabName;
          newTabData[tabName] = rfaData;
        });
        setTabData(newTabData);
        setLoading(false);
        if (isInitialLoad) setIsInitialLoad(false);
        cb();
      })
      .catch((e) => {
        console.error('Error retrieving rfas');
        errorcb();
      });
  };

  const getRFAData = (tabName, term, statuses, pageSize, offset) => {
    const {permissions, entitiesEnabled} = props;

    //filtering correct statuses for tabs
    let filteredArr = [];
    _.forEach(rfaDynamicData, (rfa) => {
      if (statuses.includes(rfa.status)) {
        filteredArr.push(rfa);
      }
    });
    const count = filteredArr.length;
    let filteredItems = [];

    //Search function
    if (term) {
      _.forEach(filteredArr, (rfa) => {
        const formattedDate =
          formatDateMMDDYYYY(_.get(rfa, 'statusTimestamp'), '') != ''
            ? formatDateMMDDYYYY(_.get(rfa, 'statusTimestamp'), '')
            : formatDateMMDDYYYY(
                ISOToDisplayDate(_.get(rfa, 'statusTimestamp') * 1000),
                ''
              );
        if (
          _.get(rfa, 'payload.subOrgDisplayName', '')
            .toLowerCase()
            .includes(term.toLowerCase()) ||
          ASSOCIATED_ENTITY_STATUSES[_.get(rfa, 'status')]
            .toLowerCase()
            .includes(term.toLowerCase()) ||
          formattedDate.includes(term) ||
          _.get(rfa, 'payload.orgDisplayName', '')
            .toLowerCase()
            .includes(term.toLowerCase())
        ) {
          filteredItems.push(rfa);
        }
      });
    } else {
      filteredItems = filteredArr;
    }

    //Sort function
    filteredItems = sortItems(filteredItems, selectedColumn, sortDirection);

    const filteredCount = filteredItems.length;

    //Slicing for Pagination
    filteredItems = _.slice(filteredItems, offset, offset + pageSize);

    const items = _.map(filteredItems, (rfa) => {
      const actions = getRfaActions(rfa, permissions, entitiesEnabled);
      return {
        ...rfa,
        actions,
      };
    });
    const rfaData = {
      tabName,
      count,
      filteredCount,
      items,
    };
    return rfaData;
  };

  const sortItems = (items, sortBy, sortDirection) => {
    let sortedItems;
    if (sortBy === 'statusTimeStamp') {
      sortedItems = _.sortBy(items, 'statusTimestamp');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }

    if (sortBy === 'status') {
      sortedItems = _.sortBy(items, 'status');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }

    if (sortBy === 'subOrgName') {
      sortedItems = _.sortBy(items, 'payload.subOrgDisplayName');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }

    if (sortBy === 'orgName') {
      sortedItems = _.sortBy(items, 'payload.orgDisplayName');
      if (sortDirection === 'DESC') sortedItems = sortedItems.reverse();
    }
    return sortedItems;
  };

  const renderAdditionalContents = (activeTab, tabs) => {
    const tabData = _.find(tabs, (data) => data.id === activeTab);
    const contents = _.get(tabData, 'contents');

    if (!contents) {
      return null;
    } else {
      return contents.map((content, contentIndex) => {
        return content ? <ContentDispatch key={contentIndex} config={content} /> : null;
      });
    }
  };

  const doEntityAction = (action, entity) => {
    const {redirectNewTab} = props;
    const entityId = entity && entity.entityId;
    switch (action) {
      case 'continueRequest':
        if (redirectNewTab) window.open(`/rfa/${entityId}/edit`, '_blank');
        else props.history.push(`/rfa/${entityId}/edit`);
        break;
      case 'viewRequest':
        if (redirectNewTab) window.open(`/rfa/${entityId}/view`, '_blank');
        else props.history.push(`/rfa/${entityId}/view`);
        break;
      default:
        console.error('Unsupported action');
    }
  };

  const renderHeader = () => {
    let headerResult = header;
    if (headerAggregate) {
      let totalCount = 0;
      _.forEach(tabsWithCount, (tab) => {
        totalCount += tab.count;
      });
      headerResult = header + ' (' + totalCount + ')';
    }
    //id={header} needed for sideNav locators to work for onClick navigation
    return (
      <Header
        as={headerSize || 'h3'}
        id={header}
        content={compileContent(headerResult, props)}
      />
    );
  };
  const toggles = _.get(props, 'toggles');
  const fmagEnhancePt2 = _.includes(toggles, 'fmagEnhancePt2');
  // Note: FMAG Enhancements Pt.3 is sharing toggles with Pt.2
  const entityEdit = _.get(props, 'entityEdit', false);
  const canStartRFA = PermissionChecks.compareRolesAndProgramsForSubEntity(
    [ROLE_MAP.SUBAUTHREP.code, ROLE_MAP.SUBMEMBER.code],
    permissions,
    organizationId,
    programId,
    organizationId
  );
  return (
    <>
      {fmagEnhancePt2 && canStartRFA && entityEdit && (
        <React.Fragment>
          <Grid>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width={8}>{header ? renderHeader() : null}</Grid.Column>
              <Grid.Column width={8} className="startSubAppLink">
                <a
                  id="startRFALink"
                  onClick={() =>
                    props.history.push('/rfaInitialize/fo/' + fundingOpportunityId)
                  }
                >
                  Start a request for fire management assistance
                </a>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </React.Fragment>
      )}
      {fmagEnhancePt2 && (!canStartRFA || !entityEdit) && (
        <React.Fragment>
          <Grid>
            <Grid.Row verticalAlign="middle">
              <Grid.Column width={8}>{header ? renderHeader() : null}</Grid.Column>
            </Grid.Row>
          </Grid>
        </React.Fragment>
      )}
      <TabComponent
        tabs={tabsWithCount}
        setActiveTab={setActiveTab}
        initialTab={activeTabId}
      />
      {renderAdditionalContents(activeTabId, tabsWithCount)}
      <div className="search-bar">
        <form id="search-form" onSubmit={onSearch}>
          <span>
            <Input>
              <Input
                aria-label={`Search by term`}
                title={`Search by term`}
                value={searchTerm}
                onChange={handleTermChange}
                onFocus={(e) => e.target.select()}
                style={{width: '380px'}}
              />
              <Button primary icon aria-label="Search Button" title="Search button">
                <Icon name="search" fitted />
              </Button>
            </Input>
            {searchTerm !== '' && (
              <a
                id="clear-search"
                href="#"
                className="padding-left"
                onClick={() => onClearSearchTerm()}
              >
                Clear search
              </a>
            )}
          </span>
        </form>
      </div>
      {loading ? (
        <div
          className="margin-top"
          style={{minHeight: tableHeight, display: 'flex', alignItems: 'center'}}
        >
          <Loader inline="centered" size="large" active>
            Loading...
          </Loader>
        </div>
      ) : (
        <>
          <div ref={tableRef}>
            <RequestsForAssistanceTable
              rfas={items}
              setSelectedColumnProp={setSelectedColumnProp}
              setSortDirectionProp={setSortDirectionProp}
              selectedColumn={selectedColumn}
              sortDirection={sortDirection}
              summary={summary}
              doEntityAction={doEntityAction}
              {...tableProps}
            />
            {_.size(items) < 1 && (
              <div id="no-rfas-found" className="text-align-center">
                {noRfaMsg}
              </div>
            )}

            {activeTabFilteredCount > pageSize && (
              <Pagination
                activePage={currentPage + 1}
                siblingRange={1}
                boundaryRange={1}
                prevItem={showPrevButton}
                nextItem={showNextButton}
                firstItem={showPrevButton}
                lastItem={showNextButton}
                ellipsisItem={undefined}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

const RequestsForAssistanceTableWrapperWithRouter = withRouter(
  RequestsForAssistanceTableWrapper
);

export default connectDynamic(RequestsForAssistanceTableWrapperWithRouter);
